import { useMemo } from 'react';

import { useAccount } from '@hooks/useAccount';
import { useBulkOptionsPricing } from '@hooks/useOptionsPricing';
import { OptionCalculator } from '@services/optionService';
import { PnLService } from '@services/pnlService';
import { priceService } from '@services/priceService';
import { volatilityService } from '@services/volatilityService';
import type { Asset, Position } from '@shared/types';

export interface AggregatedPosition {
  id: string;
  breakEvenPrice: number;
  totalQuantity: number;
  callCount: number;
  putCount: number;
  totalPnL: number;
  positions: Position[];
  averageStrike: number;
  dominantType: 'CALL' | 'PUT';
}

export function usePositionAggregation(
  asset: Asset,
  marketData: { spotPrice: number; isReady: boolean },
): AggregatedPosition[] {
  const { positions } = useAccount();
  const volatility = volatilityService.getVolatility(asset);

  // Get strike prices from current positions for bulk pricing
  const positionStrikePrices = useMemo(() => {
    const filteredPositions = positions.filter(pos => {
      if (!pos) {
        return false;
      }
      return pos.asset === asset && !OptionCalculator.isExpired(pos);
    });
    return [...new Set(filteredPositions.map(pos => pos.strikePrice))];
  }, [positions, asset]);

  // Get bulk pricing for all strikes (includes bid/ask prices)
  const _bulkPricing = useBulkOptionsPricing({
    spotPrice: marketData.spotPrice,
    strikePrices: positionStrikePrices,
    asset,
  });

  return useMemo(() => {
    // Filter positions for current asset and exclude expired ones
    const currentAssetPositions = positions.filter(pos => {
      if (!pos) {
        return false;
      }

      // Position extends Option, so properties are directly on the position object
      return pos.asset === asset && !OptionCalculator.isExpired(pos);
    });

    if (
      currentAssetPositions.length === 0 ||
      !marketData.isReady ||
      !volatility
    ) {
      return [];
    }

    // Calculate metrics for each position
    const positionsWithMetrics = currentAssetPositions
      .map(position => {
        try {
          // Use PnLService for PnL calculation
          const spotPrice = priceService.getPrice(position.asset)?.price;
          if (!spotPrice || !volatility || volatility <= 0) {
            return null;
          }

          // Calculate realistic breakeven using real volatility (not hardcoded)
          const currentBreakeven = OptionCalculator.getRealisticBreakeven(
            position,
            marketData.spotPrice,
            Date.now(),
            volatility,
          );

          return {
            position,
            breakEven: currentBreakeven,
          };
        } catch (error) {
          console.warn('Error calculating position metrics:', error, position);
          return null;
        }
      })
      .filter((item): item is NonNullable<typeof item> => item !== null); // Remove null entries

    if (positionsWithMetrics.length === 0) {
      return [];
    }

    // Separate calls and puts, then aggregate each type separately
    const callPositions = positionsWithMetrics.filter(
      ({ position }) => position.type === 'CALL',
    );
    const putPositions = positionsWithMetrics.filter(
      ({ position }) => position.type === 'PUT',
    );

    const groups: { [key: string]: typeof positionsWithMetrics } = {};

    // Aggregate all CALL positions together if any exist
    if (callPositions.length > 0) {
      groups['CALL'] = callPositions;
    }

    // Aggregate all PUT positions together if any exist
    if (putPositions.length > 0) {
      groups['PUT'] = putPositions;
    }

    // Convert groups to aggregated positions
    return Object.entries(groups).map(([type, groupPositions]) => {
      const positions = groupPositions.map(({ position }) => position);
      const totalQuantity = positions.reduce(
        (sum, pos) => sum + pos.quantity,
        0,
      );

      // Calculate aggregated PnL using PnLService
      const totalPnL = groupPositions.reduce((sum, { position }) => {
        try {
          const spotPrice = priceService.getPrice(position.asset)?.price;
          if (!spotPrice || !volatility || volatility <= 0) {
            return sum;
          }

          const pnlData = PnLService.calculatePositionPnL(
            position,
            spotPrice,
            volatility,
            Date.now(),
          );

          return sum + pnlData.pnlTotal;
        } catch (error) {
          console.warn('Error calculating position PnL:', error);
          return sum;
        }
      }, 0);

      // Use the first position's breakeven as representative for the group
      const breakEvenPrice = groupPositions[0]?.breakEven ?? 0;

      // Calculate average strike price
      const totalNotional = positions.reduce(
        (sum, pos) => sum + pos.strikePrice * pos.quantity,
        0,
      );
      const averageStrike = totalNotional / totalQuantity;

      return {
        id: `${type}_${asset}`,
        breakEvenPrice,
        totalQuantity,
        callCount: type === 'CALL' ? positions.length : 0,
        putCount: type === 'PUT' ? positions.length : 0,
        totalPnL,
        positions,
        averageStrike,
        dominantType: type as 'CALL' | 'PUT',
      };
    });
  }, [positions, asset, marketData.spotPrice, marketData.isReady, volatility]);
}
